import { Box, Button, Flex, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  InvoiceComponentWithInvoiceItemsInputGQLType,
  InvoiceInputGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { InvoiceDetailInformation } from '../InvoiceDetailInformation';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  GetInvoicesByOrganizationIdDocumentGQLType,
  GetLatestInvoiceFacilityFeeAccrualEndDateDocumentGQLType,
  useCreateInvoiceWithInvoiceItemsMutation,
} from 'packages/invoices/requests/invoices.gql.generated';
import { GetResolvedLoanByIdDocumentGQLType } from 'packages/loans/requests/loans.gql.generated';
import {
  useGetCreditFacilityExternalFacilityIdQuery,
  useGetCreditFacilityBorrowerEntityNameQuery,
} from '~/packages/credit-facility/requests/requests.gql.generated';

type ValidInvoice = {
  isValid: true;
  errors: null;
  validatedInvoice: InvoiceInputGQLType;
};

type InvalidInvoice = {
  isValid: false;
  errors: string[];
  validatedInvoice: null;
};

const _validateInvoice: (
  invoice: Omit<InvoiceInputGQLType, 'dueDate'> & {
    dueDate: Util.FullDateStr | null;
  },
  invoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[],
) => ValidInvoice | InvalidInvoice = (
  invoice,
  invoiceComponentsWithInvoiceItems,
) => {
  const errors: string[] = [];

  invoiceComponentsWithInvoiceItems.forEach((invoiceComponent) => {
    if (
      invoiceComponent.invoiceComponent.accrualEndDate <
      invoiceComponent.invoiceComponent.accrualStartDate
    ) {
      errors.push(
        `Invoice component for [${
          invoiceComponent.invoiceComponent.invoiceComponentType === 'loan'
            ? `loanId:${invoiceComponent.invoiceComponent.finleyLoanId}`
            : `facility fees`
        }] has an accrual end date that is before the accrual start date.`,
      );
    }
  });

  if (!invoice.dueDate) {
    errors.push('Invoice due date is required.');
  }

  if (errors.length > 0) {
    return {
      isValid: false,
      errors,
      validatedInvoice: null,
    };
  }

  // for type. safe to assert since the error would have already returned early
  finleyAssert(invoice.dueDate, 'Invoice due date is required.');

  return {
    isValid: true,
    errors: null,
    validatedInvoice: { ...invoice, dueDate: invoice.dueDate },
  };
};

export const CreateInvoiceReview = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      creditFacility: { creditFacility },
      bankAccounts: { destinationBankAccount },
    },
  } = useCreateInvoiceContext();

  const getExternalFacilityIdQuery = useGetCreditFacilityExternalFacilityIdQuery(
    {
      skip: !creditFacility?.id,
      variables: { creditFacilityId: creditFacility?.id ?? '' },
    },
  );
  if (getExternalFacilityIdQuery.error) {
    throw getExternalFacilityIdQuery.error;
  }

  const borrowerEntityNameQuery = useGetCreditFacilityBorrowerEntityNameQuery({
    skip: !creditFacility,
    variables: {
      creditFacilityId: creditFacility?.id ?? '',
    },
  });
  if (borrowerEntityNameQuery.error) throw borrowerEntityNameQuery.error;

  const [
    createInvoiceWithInvoiceItems,
    { loading: isCreateInvoiceLoading },
  ] = useCreateInvoiceWithInvoiceItemsMutation({
    onError: (err) => {
      Sentry.captureException(err);

      errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Unable to create invoice. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
      GetLatestInvoiceFacilityFeeAccrualEndDateDocumentGQLType,
    ],
  });

  const onClickCreateInvoice = useCallback(async () => {
    const { isValid, errors, validatedInvoice } = _validateInvoice(
      invoice,
      invoiceComponentsWithInvoiceItems,
    );

    if (!isValid) {
      toast({
        ...errorToastConfig,
        title:
          'The following errors must be fixed before creating the invoice.',
        description: errors.join('\n'),
      });
      return;
    }

    const response = await createInvoiceWithInvoiceItems({
      variables: {
        invoice: validatedInvoice,
        invoiceComponentsWithInvoiceItems,
      },
    });

    if (response.errors) {
      return;
    }

    if (response.data) {
      toast({ ...successToastConfig, title: 'Successfully created invoice' });

      const baseInvoiceDetailPath = `/invoices/${response.data.createInvoiceWithInvoiceItems.invoiceId}`;

      if (response.data.createInvoiceWithInvoiceItems.invoiceWorkleaseId) {
        // if worklease is created, redirect to the invoice detail page with the worklease id
        navigate(
          `${baseInvoiceDetailPath}/worklease/${response.data.createInvoiceWithInvoiceItems.invoiceWorkleaseId}`,
        );
      } else {
        // otherwise, redirect to the invoice detail page without the worklease id
        navigate(baseInvoiceDetailPath);
      }
    }
  }, [
    createInvoiceWithInvoiceItems,
    invoice,
    invoiceComponentsWithInvoiceItems,
    navigate,
    toast,
  ]);

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={3} />
      </Box>

      <Flex direction="column" flexGrow={1}>
        {creditFacility && invoice.dueDate && (
          <InvoiceDetailInformation
            invoice={{
              ...invoice,
              dueDate: invoice.dueDate,
              invoiceComponentsWithInvoiceItems,
              creditFacility: {
                id: creditFacility.id,
                name: creditFacility.name,
                externalFacilityId:
                  getExternalFacilityIdQuery.data?.creditFacility
                    .externalFacilityId,
              },
              destinationBankAccount: destinationBankAccount
                ? (({ __typename: _typename, ...rest }) => rest)(
                    destinationBankAccount,
                  )
                : undefined,
              paymentSourceOrganization: {
                id: creditFacility.borrowerId,
                name:
                  borrowerEntityNameQuery.data?.creditFacility
                    .borrowerEntityName ?? '',
              },
              invoiceCalculationComparisons: [],
              __typename: 'PreviewInvoice',
            }}
            isPreview
            // no need to show mismatch alert on preview
            mismatchAlertOnClick={() => {}}
            mismatchAlertOnClickLabel=""
          />
        )}
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" py="40px">
        <Button
          variant="v2-ghost"
          onClick={() => setCurrentStepIndex(2)}
          isDisabled={isCreateInvoiceLoading}
        >
          Back
        </Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          isLoading={isCreateInvoiceLoading}
          onClick={onClickCreateInvoice}
        >
          Create Invoice
        </Button>
      </Flex>
    </Flex>
  );
};
